import { createClient } from '@supabase/supabase-js'
import { useState, useEffect, createContext, useContext } from 'react'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables')
}

const isBrowser = typeof window !== 'undefined'

// Bypass navigator.locks to prevent "lock was released" errors
// when multiple async auth calls overlap.
const noOpLock = async (_name, _acquireTimeout, fn) => fn()

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    flowType: 'implicit',
    storage: isBrowser ? localStorage : undefined,
    storageKey: 'supabase.auth.token',
    lock: noOpLock,
  },
})

export const refreshSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession()
    return { data, error }
  } catch (e) {
    return { data: { session: null }, error: e }
  }
}

export const checkIsAdmin = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('admins')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.error('Error checking admin status:', error)
      return false
    }
    return !!data
  } catch (err) {
    console.error('Exception checking admin status:', err)
    return false
  }
}

// ─── Auth Context ────────────────────────────────────────────────────────────

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const checkUserAdminStatus = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('Error checking admin status:', error)
        return false
      }
      return !!data
    } catch (err) {
      console.error('Error checking admin status:', err)
      return false
    }
  }

  useEffect(() => {
    let mounted = true
    let currentUserId = null
    // Supabase sometimes fires SIGNED_OUT right before TOKEN_REFRESHED on tab
    // switch. Without a delay, user briefly becomes null → components clear
    // their data → re-fetch gets stuck. We wait 1.5 s before actually
    // clearing state; if a new session arrives in that window we cancel it.
    let signOutTimer = null

    const clearSignOutTimer = () => {
      if (signOutTimer) {
        clearTimeout(signOutTimer)
        signOutTimer = null
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        console.log('Auth state changed:', event)

        try {
          if (session?.user) {
            // Cancel any pending sign-out — session came back in time
            clearSignOutTimer()

            const userId = session.user.id

            if (userId !== currentUserId) {
              // New user or first load — update everything
              currentUserId = userId
              setUser(session.user)
              const adminStatus = await checkUserAdminStatus(userId)
              if (mounted) setIsAdmin(adminStatus)
            }
            // Same user (TOKEN_REFRESHED, tab switch) — skip state updates
            // so dependent components don't re-render and re-fetch their data
          } else {
            // Delay clearing auth state to absorb the brief SIGNED_OUT that
            // Supabase emits before TOKEN_REFRESHED on tab visibility change
            clearSignOutTimer()
            signOutTimer = setTimeout(() => {
              if (mounted && currentUserId !== null) {
                currentUserId = null
                setUser(null)
                setIsAdmin(false)
              }
            }, 1500)
          }
        } catch (err) {
          console.error('Error in auth state change handler:', err)
          setError(err.message)
        } finally {
          if (mounted) setLoading(false)
        }
      }
    )

    return () => {
      mounted = false
      clearSignOutTimer()
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email, password) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })

      if (error) throw error
      if (!data.user) throw new Error('No user returned')

      const isUserAdmin = await checkUserAdminStatus(data.user.id)

      if (!isUserAdmin) {
        await supabase.auth.signOut()
        throw new Error('Доступ запрещен. Вы не являетесь администратором.')
      }

      setUser(data.user)
      setIsAdmin(true)
      return data
    } catch (err) {
      setError(err.message || 'Ошибка входа')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      await supabase.auth.signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    } finally {
      setUser(null)
      setIsAdmin(false)
      setLoading(false)
    }
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}